/**
 * Step 4 (Platform-Eng workflow) — RegoEditorSlideout.
 *
 * <p>Closes the Tier-1 gap the platform-engineer walkthrough flagged
 * as "the worst one for the scenario": until this slideout shipped,
 * authoring a Rego rule meant `curl -X POST /api/v1/policy/rules`
 * with `rego_source` as a body field. There was no UI surface at all.
 * Now an SRE can:</p>
 *
 * <ol>
 *   <li>Open the slideout from the Policy Skills tab.</li>
 *   <li>Author Rego in Monaco (with `rego` language registration —
 *       keywords, operators, comments, strings all highlighted).</li>
 *   <li>Hit "Smoke evaluate" to run the module against a JSON input
 *       bundle WITHOUT persisting (POST /api/v1/policy/rules/rego/test).</li>
 *   <li>See the verdict pill (ALLOW / DENY / UNKNOWN) + reason +
 *       details object inline.</li>
 *   <li>Save — which two-steps: create the rule shell (CUSTOM_SCRIPT
 *       type, BLOCK severity, sensible defaults), then PUT the Rego
 *       module (which runs the OPA dry-run validation server-side
 *       and stamps regoLastValidatedAt).</li>
 *   <li>Optionally bind to a skill_id or agent_id at save time
 *       (POST /api/v1/policy/bindings).</li>
 * </ol>
 *
 * <h3>Edit mode</h3>
 *
 * <p>When opened with {@code existingRule={...}}, the slideout
 * pre-fills the editor from {@code rule.regoModule} +
 * {@code rule.regoQuery} and the Save button only calls
 * putRuleRego — the rule shell already exists, so the create +
 * binding steps are skipped.</p>
 *
 * <h3>Lazy Monaco + textarea fallback</h3>
 *
 * <p>Monaco is lazy-loaded so opening /policy-config doesn't pay
 * the ~2 MB editor cost. If the bundle fails to resolve (offline
 * dev, broken node_modules, CDN block), the slideout transparently
 * falls back to a styled {@code <textarea>} — same wire shape, no
 * syntax highlighting. The smoke-eval + save paths still work.</p>
 *
 * <h3>Capability guard</h3>
 *
 * <p>If {@code agentlets.opa.url} is not configured on the server,
 * {@code getRegoCapability().configured === false}. The slideout
 * still opens (so an operator can still draft Rego), but the
 * Smoke evaluate button is disabled with an inline banner
 * explaining the OPA daemon isn't wired.</p>
 */
import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import {
  Save, Play, AlertTriangle, CheckCircle, Loader2, FileCode2,
  Link2, ShieldCheck, ShieldX, HelpCircle,
} from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Card } from '../Card';
import { Pill } from '../Pill';
import {
  getRegoCapability, testRego, putRuleRego,
  createPolicyRule, createPolicyBinding,
} from '../../../services/api';

/* Lazy-load Monaco. Same pattern as WorkflowCodeTab — keeps the
 * /policy-config first-paint cheap. The graceful fallback ensures
 * a missing/broken Monaco doesn't kill the slideout outright. */
const MonacoEditor = lazy(() =>
  import('@monaco-editor/react')
    .then((mod) => ({ default: mod.default || mod.Editor || mod }))
    .catch(() => ({ default: () => null })),
);

/* Step 4 — Monarch tokenizer for Rego. Registered once on the first
 * Monaco mount; subsequent mounts are idempotent (Monaco's
 * registerLanguage no-ops on duplicate ids). Keywords pulled from
 * the Rego language spec; operators include the assignment forms
 * (:=, =, ==, !=) the OPA parser distinguishes between. */
function registerRegoLanguage(monaco) {
  if (!monaco?.languages) return;
  const langs = monaco.languages.getLanguages();
  if (langs.some((l) => l.id === 'rego')) return; // already registered

  monaco.languages.register({ id: 'rego', extensions: ['.rego'], aliases: ['Rego', 'rego', 'OPA'] });
  monaco.languages.setMonarchTokensProvider('rego', {
    keywords: [
      'package', 'import', 'as', 'default', 'else', 'false', 'if',
      'in', 'not', 'null', 'some', 'every', 'true', 'with',
    ],
    builtins: [
      'allow', 'deny', 'input', 'data', 'count', 'sum', 'sprintf',
      'startswith', 'endswith', 'contains', 'regex',
    ],
    operators: [':=', '=', '==', '!=', '<', '>', '<=', '>=', '+', '-', '*', '/', '%', '|'],
    symbols: /[=><!~?:&|+\-*/^%]+/,
    tokenizer: {
      root: [
        [/\b(?:[A-Za-z_][A-Za-z0-9_]*)\b/, {
          cases: {
            '@keywords': 'keyword',
            '@builtins': 'type',
            '@default':  'identifier',
          },
        }],
        [/"([^"\\]|\\.)*$/, 'string.invalid'],
        [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
        [/#.*$/, 'comment'],
        [/\d+(?:\.\d+)?/, 'number'],
        [/@symbols/, {
          cases: {
            '@operators': 'operator',
            '@default':   '',
          },
        }],
        [/[{}()[\]]/, '@brackets'],
        [/[;,.]/, 'delimiter'],
        [/\s+/, 'white'],
      ],
      string: [
        [/[^\\"]+/, 'string'],
        [/\\./, 'string.escape'],
        [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
      ],
    },
  });
}

/* Step 4 — pill styling for the smoke-eval verdict. Matches the
 * backend's RegoDecision.Kind enum (ALLOW / DENY / UNKNOWN). */
const VERDICT_PILL = {
  ALLOW:   { tone: 'success', icon: ShieldCheck },
  DENY:    { tone: 'danger',  icon: ShieldX },
  UNKNOWN: { tone: 'warning', icon: HelpCircle },
};

/* DEF-EXEC-011 — `import rego.v1` is required for the `allow if { ... }`
 * keyword form to parse under OPA 0.69+. Without it, OPA evaluates the
 * module and produces `undefined` for the allow rule, leaving only the
 * `default allow := false` fallback — which makes the smoke-eval pill
 * read DENY with reason "produced no result (undefined)" and confuses
 * the author into thinking their rule is wrong.
 *
 * `rego.v1` is the Rego 1.0 dialect import: it bundles `if`, `in`,
 * `every`, and `contains` keywords AND enforces `:=` for assignment.
 * Equivalent older form: `import future.keywords.if` (Rego v0). We
 * prefer `rego.v1` because (a) it's the forward-compatible canonical
 * choice as of OPA 1.0, (b) it imports all the keywords we use here,
 * and (c) the OPA team has signalled future deprecation of the
 * `future.keywords` family. */
const DEFAULT_MODULE = `# Rego policy authored from the Agentlets Policy Skills tab.
# Default deny; override with explicit allow rules below.
package agentlets.policy

import rego.v1

default allow := false

# Example: allow when the SRE's role is platform-admin and the skill
# isn't archived. Replace with your actual decision logic.
allow if {
  input.user.role == "platform-admin"
  input.skill.archived == false
}
`;

const DEFAULT_QUERY = 'data.agentlets.policy.allow';

const DEFAULT_INPUT = `{
  "user":  { "id": "u-1", "role": "platform-admin" },
  "skill": { "id": "s-1", "archived": false },
  "agent": { "id": "a-1" }
}`;

const FALLBACK_TEXTAREA_CLASS =
  'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface ' +
  'text-v2-ink text-[12px] font-mono-v2 whitespace-pre';

/**
 * Step 4 — props.
 *
 * @param {boolean} isOpen           — slideout visibility (controlled).
 * @param {fn}      onClose          — close handler (controlled).
 * @param {fn}      onSaved          — invoked after a successful save
 *                                     with the persisted rule row.
 * @param {object?} existingRule     — when provided, slideout enters
 *                                     EDIT mode. Pre-fills from
 *                                     rule.regoModule + rule.regoQuery.
 * @param {string?} presetSkillId    — when provided in CREATE mode,
 *                                     pre-fills the skill binding so
 *                                     the new rule auto-attaches.
 * @param {string?} presetAgentId    — same, for agent binding.
 */
export function RegoEditorSlideout({
  isOpen, onClose, onSaved,
  existingRule = null,
  presetSkillId = null,
  presetAgentId = null,
}) {
  const isEdit = !!existingRule?.id;

  // Capability probe + form state -----------------------------------
  const [capability, setCapability] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [regoModule, setRegoModule] = useState(DEFAULT_MODULE);
  const [regoQuery, setRegoQuery]   = useState(DEFAULT_QUERY);
  const [testInput, setTestInput]   = useState(DEFAULT_INPUT);
  const [bindSkillId, setBindSkillId] = useState(presetSkillId || '');
  const [bindAgentId, setBindAgentId] = useState(presetAgentId || '');

  // Lifecycle state -------------------------------------------------
  const [loadingCap, setLoadingCap] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [smokeRunning, setSmokeRunning] = useState(false);
  const [smokeResult, setSmokeResult] = useState(null);
  const [smokeError, setSmokeError]   = useState(null);
  const [monacoFailed, setMonacoFailed] = useState(false);

  /* Re-seed state every time the slideout opens (CREATE) or when
   * the edit target changes (EDIT). React's component-instance
   * persistence across multiple opens would otherwise leak the
   * previous draft into a fresh authoring session. */
  useEffect(() => {
    if (!isOpen) return;
    setSaving(false);
    setSaveError(null);
    setSmokeResult(null);
    setSmokeError(null);

    if (isEdit) {
      setName(existingRule.name || '');
      setDescription(existingRule.description || '');
      setRegoModule(existingRule.regoModule || DEFAULT_MODULE);
      setRegoQuery(existingRule.regoQuery || DEFAULT_QUERY);
    } else {
      setName('');
      setDescription('');
      setRegoModule(DEFAULT_MODULE);
      setRegoQuery(DEFAULT_QUERY);
      setTestInput(DEFAULT_INPUT);
      setBindSkillId(presetSkillId || '');
      setBindAgentId(presetAgentId || '');
    }
  }, [isOpen, isEdit, existingRule, presetSkillId, presetAgentId]);

  /* Capability probe runs once per open. Cheap GET; result tells
   * us whether to disable the smoke-eval button + show a banner. */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingCap(true);
    getRegoCapability().then((cap) => {
      if (cancelled) return;
      /* DEF-EXEC-006 fix — apiFetch wraps every successful response in
       * an envelope `{ok:true, data:{...}}` (services/api.js line 205).
       * The capability payload `{configured, queryDefault}` therefore
       * lives under `cap.data`, not directly on `cap`. The previous
       * `setCapability(cap)` stored the envelope as-is, so the
       * `opaConfigured = capability?.configured === true` check below
       * always evaluated `undefined === true → false`. Result: the UI
       * said "OPA not configured" even when the backend reported
       * configured=true. Unwrap to the inner payload. */
      setCapability(cap?.ok === false ? null : (cap?.data ?? cap));
      setLoadingCap(false);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  // ── Handlers ────────────────────────────────────────────────────

  const onSmokeEvaluate = useCallback(async () => {
    setSmokeRunning(true);
    setSmokeError(null);
    setSmokeResult(null);
    let parsedInput = null;
    if (testInput && testInput.trim()) {
      try { parsedInput = JSON.parse(testInput); }
      catch (e) {
        setSmokeError(`Test input is not valid JSON: ${e.message}`);
        setSmokeRunning(false);
        return;
      }
    }
    const r = await testRego({ regoModule, regoQuery, input: parsedInput });
    if (r?.ok === false) {
      setSmokeError(r.error || 'Smoke evaluate failed');
      setSmokeRunning(false);
      return;
    }
    /* DEF-EXEC-006 fix — apiFetch wraps successful responses in
     * `{ok:true, data:{verdict, reason, details}}`. Storing the raw
     * envelope means the `smokeResult.verdict` read at line ~524
     * always falls back to "UNKNOWN" even when OPA correctly returns
     * ALLOW or DENY. Unwrap to the inner payload here. */
    setSmokeResult(r?.data ?? r);
    setSmokeRunning(false);
  }, [regoModule, regoQuery, testInput]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    // ── EDIT path: just PUT the new Rego onto the existing rule.
    if (isEdit) {
      const r = await putRuleRego(existingRule.id,
                                  { regoModule, regoQuery });
      if (r?.ok === false) {
        setSaveError(r.error || 'Save failed');
        setSaving(false);
        return;
      }
      setSaving(false);
      if (typeof onSaved === 'function') onSaved(r);
      onClose?.();
      return;
    }

    // ── CREATE path:
    //   1. POST /rules to create the shell (CUSTOM_SCRIPT type).
    //   2. PUT  /rules/{id}/rego to attach Rego (runs dry-run).
    //   3. POST /bindings (if skill or agent target was picked).
    if (!name.trim()) {
      setSaveError('Name is required.');
      setSaving(false);
      return;
    }

    const created = await createPolicyRule({
      name: name.trim(),
      description: description.trim() || null,
      ruleType: 'CUSTOM_SCRIPT',
      severity: 'BLOCK',
      targetField: 'input',
      // Empty config object; the Rego module IS the logic.
      ruleConfig: {},
      enforcementPoint: 'ALL',
      priority: 100,
      enabled: true,
    });
    if (created?.ok === false) {
      setSaveError(created.error || 'Create rule shell failed');
      setSaving(false);
      return;
    }
    const newId = created?.id;
    if (!newId) {
      setSaveError('Backend did not return a new rule id');
      setSaving(false);
      return;
    }

    const attached = await putRuleRego(newId, { regoModule, regoQuery });
    if (attached?.ok === false) {
      setSaveError(
        `Rule created (${newId.slice(0, 8)}…) but Rego attachment failed: `
        + (attached.error || 'unknown'));
      setSaving(false);
      return;
    }

    // Optional binding step. Skip on validation errors of the
    // inputs — partial success is better than full rollback for
    // an authored rule the operator has invested time in.
    const skill = bindSkillId.trim();
    const agent = bindAgentId.trim();
    if (skill || agent) {
      const binding = await createPolicyBinding({
        policyRuleId: newId,
        scope: 'CUSTOM',
        skillId: skill || null,
        agentId: agent || null,
      });
      if (binding?.ok === false) {
        setSaveError(
          `Rule + Rego saved, but binding failed: `
          + (binding.error || 'unknown'));
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    if (typeof onSaved === 'function') onSaved(attached);
    onClose?.();
  }, [
    isEdit, existingRule, name, description, regoModule, regoQuery,
    bindSkillId, bindAgentId, onSaved, onClose,
  ]);

  // ── Renders ─────────────────────────────────────────────────────

  const inputClass =
    'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border ' +
    'bg-v2-surface text-v2-ink text-[13px]';
  const codeFieldClass = FALLBACK_TEXTAREA_CLASS;

  const opaConfigured = capability?.configured === true;

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={isEdit ? 'Edit Rego rule' : 'Author Rego rule'}
      width={720}
    >
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FileCode2 size={16} className="text-v2-action-strong" />
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isEdit ? 'Edit Rego rule' : 'Author new Rego rule'}
          </h2>
          {!loadingCap && (
            <Pill tone={opaConfigured ? 'success' : 'warning'} size="sm" dot>
              {opaConfigured ? 'OPA connected' : 'OPA not configured'}
            </Pill>
          )}
        </div>

        {!opaConfigured && !loadingCap && (
          <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft border border-v2-warning/30 text-[12px] text-v2-warning-strong flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              {(capability?.error || 'agentlets.opa.url is not configured on this server.')}
              {' '}You can still draft the module, but Smoke evaluate and the
              dry-run validation at save will both be unavailable until
              OPA is wired.
            </span>
          </div>
        )}

        {/* CREATE-only metadata fields */}
        {!isEdit && (
          <Card title="Rule metadata">
            <div className="p-3 space-y-3">
              <label className="block">
                <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                  Name *
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. platform-admin-only-wasm-fleet-controller"
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                  Description
                </span>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="One-line summary the on-call SRE will see in the audit chain."
                  className={inputClass}
                />
              </label>
            </div>
          </Card>
        )}

        {/* Rego module editor */}
        <Card title="Rego module">
          <div className="p-3 space-y-2">
            <div style={{ height: 320 }} className="border border-v2-border rounded-v2-sm overflow-hidden">
              {monacoFailed ? (
                <textarea
                  value={regoModule}
                  onChange={(e) => setRegoModule(e.target.value)}
                  className={`${codeFieldClass} h-full block resize-none`}
                />
              ) : (
                <Suspense fallback={
                  <div className="h-full flex items-center justify-center text-[12px] text-v2-ink-muted">
                    <Loader2 size={14} className="animate-spin mr-2" /> Loading editor…
                  </div>
                }>
                  <MonacoEditor
                    height="100%"
                    language="rego"
                    value={regoModule}
                    onChange={(v) => setRegoModule(v ?? '')}
                    beforeMount={(monaco) => registerRegoLanguage(monaco)}
                    onValidate={() => { /* no-op */ }}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 12,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      tabSize: 2,
                    }}
                    /* If Monaco's bundle resolves but the component
                     * fails to render (rare), fall through to the
                     * textarea on the next state update. */
                    onMount={(_editor, monaco) => {
                      if (!monaco) setMonacoFailed(true);
                    }}
                  />
                </Suspense>
              )}
            </div>
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                Rego query
              </span>
              <input
                type="text"
                value={regoQuery}
                onChange={(e) => setRegoQuery(e.target.value)}
                placeholder={DEFAULT_QUERY}
                className={`${inputClass} font-mono-v2`}
              />
            </label>
          </div>
        </Card>

        {/* Smoke evaluate pane */}
        <Card title="Smoke evaluate against test input">
          <div className="p-3 space-y-3">
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                Test input (JSON)
              </span>
              <textarea
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                rows={6}
                className={`${codeFieldClass} resize-y`}
              />
            </label>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary" size="sm"
                disabled={smokeRunning || !opaConfigured}
                onClick={onSmokeEvaluate}
                iconLeft={smokeRunning
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Play size={13} />}
              >
                {smokeRunning ? 'Evaluating…' : 'Smoke evaluate'}
              </Button>
              {smokeResult && (() => {
                const k = smokeResult.verdict || 'UNKNOWN';
                const v = VERDICT_PILL[k] || VERDICT_PILL.UNKNOWN;
                const Icon = v.icon;
                return (
                  <Pill tone={v.tone} size="sm" dot>
                    <Icon size={11} style={{ display: 'inline', marginRight: 4 }} />
                    {k}
                  </Pill>
                );
              })()}
            </div>
            {smokeError && (
              <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[11.5px] text-v2-danger-strong flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>{smokeError}</span>
              </div>
            )}
            {smokeResult && (
              <div className="text-[11.5px] space-y-1">
                {smokeResult.reason && (
                  <div>
                    <span className="text-v2-ink-muted">Reason: </span>
                    <span className="text-v2-ink">{smokeResult.reason}</span>
                  </div>
                )}
                {smokeResult.details && (
                  <details>
                    <summary className="text-v2-ink-muted cursor-pointer">Details</summary>
                    <pre className="mt-1 px-2 py-1 bg-v2-surface-muted rounded-v2-sm font-mono-v2 text-[11px] overflow-x-auto">
                      {JSON.stringify(smokeResult.details, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Binding scope — CREATE only. EDIT preserves existing
            bindings (bindings live in their own table, decoupled). */}
        {!isEdit && (
          <Card title="Binding (optional)">
            <div className="p-3 space-y-3">
              <p className="text-[11.5px] text-v2-ink-muted m-0 flex items-start gap-2">
                <Link2 size={12} className="mt-0.5 shrink-0" />
                Pin this rule to a single skill or agent. Leave both empty
                to leave the rule unbound — you can attach it later from
                the Policy Skills inventory.
              </p>
              <label className="block">
                <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                  Skill ID
                </span>
                <input
                  type="text"
                  value={bindSkillId}
                  onChange={(e) => setBindSkillId(e.target.value)}
                  placeholder="UUID (optional)"
                  className={`${inputClass} font-mono-v2`}
                />
              </label>
              <label className="block">
                <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                  Agent ID
                </span>
                <input
                  type="text"
                  value={bindAgentId}
                  onChange={(e) => setBindAgentId(e.target.value)}
                  placeholder="UUID (optional)"
                  className={`${inputClass} font-mono-v2`}
                />
              </label>
            </div>
          </Card>
        )}

        {saveError && (
          <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-danger-strong flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary" size="sm"
            disabled={saving || (!isEdit && !name.trim())}
            onClick={onSave}
            iconLeft={saving
              ? <Loader2 size={13} className="animate-spin" />
              : <Save size={13} />}
          >
            {saving
              ? 'Saving…'
              : isEdit ? 'Save Rego changes' : 'Create rule with Rego'}
          </Button>
        </div>
      </div>
    </Slideout>
  );
}

export default RegoEditorSlideout;
